import { type ReactNode, type JSX } from 'react';
import { useTheme } from '../../../../theme';
import { InfoIcon } from '../../infoIcon';
import Box from '@mui/material/Box';
import { type InfoIconPopoverPlacement } from '../../infoIcon';
import { BodyS } from '../../../../typography';

export type FormLabelProps = {
  labelId?: string;
  htmlFor?: string;
  label?: string | null;
  required?: boolean;
  labelTooltip?: string | ReactNode;
  labelTooltipPosition?: InfoIconPopoverPlacement;
  labelBadge?: ReactNode;
  /* Used in the rare case where a label isn't required e.g. check-in name / passcode inputs where there are
     headings descriptive enough. This hidden label still allows screen readers to work for accessibility */
  labelVisuallyHidden?: boolean;
};

export const FormLabel = ({
  labelId,
  htmlFor,
  label,
  required = false,
  labelTooltip,
  labelTooltipPosition,
  labelBadge,
  labelVisuallyHidden = false,
}: FormLabelProps): JSX.Element | null => {
  const theme = useTheme();

  if (!Boolean(label)) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        columnGap: theme.spacing(1),
      }}
    >
      <BodyS
        tag='label'
        id={labelId}
        htmlFor={htmlFor}
        fontWeight='medium'
        color={theme.text.generic.secondary}
        css={
          labelVisuallyHidden && {
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            whiteSpace: 'nowrap',
            width: 1,
          }
        }
      >
        <span>{label}</span>
      </BodyS>
      {!labelVisuallyHidden && (
        <>
          {required && <span>*</span>}
          {labelTooltip !== undefined && (
            <InfoIcon
              title={labelTooltip}
              aria-label={
                typeof labelTooltip === 'string' ? labelTooltip : undefined
              }
              popoverPlacement={labelTooltipPosition}
            />
          )}
          {Boolean(labelBadge) && (
            <span css={{ marginLeft: theme.spacing(1) }}>{labelBadge}</span>
          )}
        </>
      )}
    </Box>
  );
};
